import { isNumber } from 'lodash';
import React, { FC, useRef, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { pensionDataStore } from 'src/store/pension';
import { dateCalendarFormatType, currentFormatTime, dateFormat } from 'src/utils/dateSet';
import { getMemberInfo } from 'src/utils/common';
import {
  errorModalViewStore,
  loadingModalViewStore,
  confirmModalViewStore,
  contentModalDep2ViewStore,
} from 'src/store/modal';
import { fetchInsertPension, fetchUpdatePension } from 'src/fetch/stepC/pension';

import InputBox from 'src/components/atoms/input/DefInputBox';
import DefaultButton from 'src/components/atoms/buttons/DefaultButton';
import LabelSelect from 'src/components/atoms/selectbox/LabelSelect';
import DefLabel from 'src/components/atoms/label/LabelContnet';
import CheckBox from 'src/components/atoms/CheckBox';
import HelpButton from 'src/components/atoms/buttons/HelpButton';
import Description from 'src/components/molecules/table/Description/PensionSaving';
import InputDateBox from 'src/components/atoms/input/InputDateBox';
import {
  settlementNtsDataStore,
  ntsFileDataStore,
  settlementDataStore,
} from 'src/store/settlementInfo';
import { addComma } from 'src/utils/common';

interface Props {
  data: any;
  type: string;
}
const GridAdd: FC<Props> = ({ data, type }) => {
  const userInfoData: any = getMemberInfo();
  const queryClient = useQueryClient();
  const errorModalState = errorModalViewStore((state: any) => state.set);
  const loadingState = loadingModalViewStore((state: any) => state.set);
  const setConfirmModalState = confirmModalViewStore((state: any) => state.set);
  const closeModalState = confirmModalViewStore((state: any) => state.close);
  const setContentModalState = contentModalDep2ViewStore((state: any) => state.set);

  const storeData = pensionDataStore((state: any) => state.result).data || [];
  const storeInitPayload = pensionDataStore((state: any) => state.initPayload);
  const storePayload = pensionDataStore((state: any) => state.payload);
  const storePayloadBind = pensionDataStore((state: any) => state.bindPayload);
  const bindStoreParam = pensionDataStore((state: any) => state.param);
  const bindStoreBindParam = pensionDataStore((state: any) => state.bindParam);

  // 정산결과
  const settlementBindParam = settlementDataStore((state: any) => state.bindParam);
  const settlementParam = settlementDataStore((state: any) => state.param);
  // 정산결과(NTS)
  const ntsInfoBindParam = settlementNtsDataStore((state: any) => state.bindParam);
  const ntsInfoParam = settlementNtsDataStore((state: any) => state.param);
  // NTS 업로드파일 데이터
  const ntsFileBindParam = ntsFileDataStore((state: any) => state.bindParam);
  const ntsFileParam = ntsFileDataStore((state: any) => state.param);

  useEffect(() => {
    if (type === 'insert') {
      storePayloadBind({ ...storeInitPayload });
    } else {
      const storeData = data;
      for (const key in storePayload) {
        if (storeData) storePayload[key] = storeData[key];
      }
      storePayloadBind({ ...storePayload });
    }
  }, [storeData]);

  const familyDataSet = storeData;
  const familySet = familyDataSet.map((item: any) => {
    return { id: item.family_seqid, value: item.name };
  });
  familySet.unshift({ id: '', value: '선택' });
  const checkedFamily =
    storePayload.family_seqid === 0
      ? familySet[0]
      : { id: storePayload.family_seqid, value: storePayload.name };

  const familySelect = {
    checked: checkedFamily,
    options: familySet,
  };

  const dataTypeSet = [
    { id: '', value: '선택' },
    {
      id: 'G0009',
      value: '개인연금저축',
    },
    {
      id: 'G0011',
      value: '퇴직연금',
    },
    {
      id: 'G0012',
      value: '과학기술인공제',
    },
    {
      id: 'PN001',
      value: '국민연금(지역가입자)',
    },
    {
      id: 'PH001',
      value: '기타건강보험',
    },
    {
      id: 'PS001',
      value: '사립학교교직원연금(추가납입분)',
    },
  ];

  const checkedDataType =
    storePayload.data_type === ''
      ? dataTypeSet[0]
      : {
          id: storePayload.data_type,
          value: dataTypeSet.find((item: any) => item.id === storePayload.data_type)?.value,
        };

  const dataTypeSelect: any = {
    checked: checkedDataType,
    options: dataTypeSet,
  };

  const help = {
    content: `
      <div className="_hint_content">
        <div className="_item">ㆍ 정치자금기부금, 고향사랑기부금, 우리사주조합기부금은 본인 지출액만 공제가능합니다.</div>
        <div className="_item">ㆍ 『기부장려금 신청금액』은 기부자의 세액공제 대신 기부금 단체에 환급하기 위해 신청한 금액입니다.</div>
        <div className="_item">ㆍ 금액(국세청·기타)에는 『기부장려금 신청금액』과의 합산이 아닌 제외된 공제대상금액을 입력합니다.</div>
      </div>
      `,
    title: '공제 대상 항목',
  };

  const help_t2 = {
    content: `
      <div className="_hint_content">
        <div className="_item">* 사업자 등록번호(주민등록번호)는 '-'를 제외한 숫자만 입력해야 합니다.</div>
      </div>
      `,
    title: '사업자등록번호',
  };

  const confirmSelect = (item: any, field: string) => {
    if (field === 'family_seqid') {
      storePayloadBind({ ...storeInitPayload, family_seqid: item.id, name: item.value });
    } else {
      storePayload[field] = item.id;
      storePayloadBind({ ...storePayload });
    }
  };

  const confirmEvt = () => {
    storePayload.acct_year = userInfoData.acct_year;
    storePayload.co_cd = userInfoData.co_cd;
    storePayload.co_id = userInfoData.co_id;
    const param: any = storePayload;

    // return false;
    fetchInsertPension(param, queryClient, errorModalState, loadingState, () => {
      const initialSeq = bindStoreParam.initialSeq || 0;
      bindStoreBindParam({ initialSeq: initialSeq + 1 });

      const initialSetSeq = settlementParam.initialSeq || 0;
      settlementBindParam({ initialSeq: initialSetSeq + 1 });
      const initialNtsSeq = ntsInfoParam.initialSeq || 0;
      ntsInfoBindParam({ initialSeq: initialNtsSeq + 1 });
      const initialFileSeq = ntsFileParam.initialSeq || 0;
      ntsFileBindParam({ initialSeq: initialFileSeq + 1 });

      setContentModalState({
        active: '',
      });
    });
  };

  // UPDATE
  const modifyEvt = () => {
    storePayload.acct_year = userInfoData.acct_year;
    storePayload.co_cd = userInfoData.co_cd;
    storePayload.co_id = userInfoData.co_id;
    const param: any = storePayload;

    // return false;
    fetchUpdatePension(param, queryClient, errorModalState, loadingState, () => {
      const initialSeq = bindStoreParam.initialSeq || 0;
      bindStoreBindParam({ initialSeq: initialSeq + 1 });

      const initialSetSeq = settlementParam.initialSeq || 0;
      settlementBindParam({ initialSeq: initialSetSeq + 1 });
      const initialNtsSeq = ntsInfoParam.initialSeq || 0;
      ntsInfoBindParam({ initialSeq: initialNtsSeq + 1 });
      const initialFileSeq = ntsFileParam.initialSeq || 0;
      ntsFileBindParam({ initialSeq: initialFileSeq + 1 });

      setContentModalState({
        active: '',
      });
    });
  };

  const checkClickEvt = (e: React.MouseEvent, type: string) => {
    let useYN = 'Y';
    if (type === 'on') useYN = 'N';
    storePayloadBind({ ...storePayload, is_htx_pdf: useYN });
  };
  const checkSchoolClickEvt = (e: React.MouseEvent, type: string) => {
    let useYN = 'Y';
    if (type === 'on') useYN = 'N';
    storePayloadBind({ ...storePayload, is_private_school: useYN });
  };

  const inpStrChangeEvt = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const chkValue = e.target?.value || '';
    storePayload[key] = chkValue;
    storePayloadBind({ ...storePayload });
  };
  const inpChangeEvt = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    let chkValue = e.target?.value || '';
    chkValue = chkValue.replaceAll(',', '');
    const chkNum = Number(chkValue);
    if (chkNum || chkNum === 0) {
      storePayload[key] = chkNum;
      storePayloadBind({ ...storePayload });
    }
  };
  const calendarStartRef = useRef<HTMLInputElement | null>(null);
  const calendarStartEvt = (date: string) => {
    const dateStr = dateFormat(date, 'YYYYMMDD');
    storePayload.sbp_date = dateStr;
    storePayloadBind({ ...storePayload });
  };

  return (
    <div className="_add_credit_layout">
      {/* <div className="_desc_div">
        <div className="_main">총 급여의 25% : 10,000,000</div>
        <div className="_sub">신용카드등은 총급여의 25% 이상이어야 공제 가능합니다.</div>
      </div> */}
      <div className="_title_div">
        <div className="_title">공제 대상 항목</div>
        <div className="_help_div">
          <HelpButton content={help.content} title={help.title} styleDef={{ width: '660px' }} />
        </div>
      </div>
      <div className="_inp_div">
        <div className="_opt_div">
          <div className="_item" style={{ zIndex: 15, width: '450px' }}>
            <LabelSelect
              label="구분"
              select={dataTypeSelect}
              labelStyle={{ width: '200px' }}
              boxStyleDef={{ width: 'calc(100% - 200px)' }}
              confirmEvent={confirmSelect}
              field="data_type"
            />
          </div>
          <div className="_item" style={{ zIndex: 5, width: '450px' }}>
            <InputBox
              changeEvt={e => inpStrChangeEvt(e, 'bank_cd')}
              label="금융기관"
              status={''}
              labelPosition="left"
              value={storePayload.bank_cd}
              placeholder="값을 입력하세요"
              labelStyleDef={{ width: '200px', color: '#333', fontSize: '14px' }}
              inpStyleDef={{ textAlign: 'right' }}
            />
          </div>
          <div className="_item" style={{ zIndex: 5, width: '450px' }}>
            <InputBox
              changeEvt={e => inpStrChangeEvt(e, 'account_num')}
              label="계좌번호"
              status={''}
              labelPosition="left"
              value={storePayload.account_num}
              placeholder="값을 입력하세요"
              labelStyleDef={{ width: '200px', color: '#333', fontSize: '14px' }}
              inpStyleDef={{ textAlign: 'right' }}
            />
          </div>

          <div className="_item" style={{ zIndex: 5, width: '450px' }}>
            <InputBox
              changeEvt={e => inpChangeEvt(e, 'htx_amt')}
              // inputRef={inpRef}
              label="금액(국세청)"
              status={''}
              labelPosition="left"
              value={addComma(storePayload.htx_amt)}
              placeholder="값을 입력하세요"
              labelStyleDef={{ width: '200px', color: '#333', fontSize: '14px' }}
              // styleDef={boxStyle}
              inpStyleDef={{ textAlign: 'right' }}
            />
          </div>
          <div className="_item" style={{ zIndex: 5, width: '450px' }}>
            <InputBox
              changeEvt={e => inpChangeEvt(e, 'amt')}
              // inputRef={inpRef}
              label="금액(기타)"
              status={''}
              labelPosition="left"
              value={addComma(storePayload.amt)}
              placeholder="값을 입력하세요"
              labelStyleDef={{ width: '200px', color: '#333', fontSize: '14px' }}
              // styleDef={boxStyle}
              inpStyleDef={{ textAlign: 'right' }}
            />
          </div>
          <div className="_item" style={{ zIndex: 5, width: '450px' }}>
            <CheckBox
              label="국세청 자료 여부"
              selected={storePayload.is_htx_pdf === 'Y' ? 'on' : 'off'}
              btnClickEvt={checkClickEvt}
              labelStyleDef={{ color: '#333', fontWeight: '500', width: '200px' }}
            />
          </div>
          <div className="_item" style={{ zIndex: 5, width: '450px' }}>
            <CheckBox
              label="관리자 사학연금 업로드 여부"
              selected={storePayload.is_private_school === 'Y' ? 'on' : 'off'}
              btnClickEvt={checkSchoolClickEvt}
              labelStyleDef={{ color: '#333', fontWeight: '500', width: '200px' }}
            />
          </div>
        </div>
      </div>
      <div className="_description">
        <Description />
      </div>
      <div className="_btn_div">
        {type === 'insert' ? (
          <DefaultButton
            text="내역 추가"
            styleDef={{ width: '250px' }}
            clickEvent={e => confirmEvt()}
            icon=""
            type="type5"
          />
        ) : (
          <DefaultButton
            text="내역 변경"
            styleDef={{ width: '250px' }}
            clickEvent={e => modifyEvt()}
            icon=""
            type="type3"
          />
        )}
      </div>
    </div>
  );
};

export default GridAdd;
